import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, AlertTriangle, Info } from 'lucide-react';
import api from '../../lib/api';

interface TokenHolding {
  tokenId: string;
  name: string;
  symbol: string;
  quantity: number;
  unitPrice: number;
  currentValue: number;
  avgBuyPrice: number;
  profitLoss: number;
  profitLossPercent: number;
  tradingFee: number;
  lockUpEndDate: string | null;
  lockedQuantity: number;
}

export function SellToken() {
  const { tokenId } = useParams<{ tokenId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [quantity, setQuantity] = useState('');
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [limitPrice, setLimitPrice] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const { data: holding, isLoading } = useQuery({
    queryKey: ['token-holding', tokenId],
    queryFn: async () => {
      const res = await api.get(`/tokenization/portfolio/holdings/${tokenId}`);
      return res.data as TokenHolding;
    },
    enabled: !!tokenId,
  });

  const sellMutation = useMutation({
    mutationFn: async (data: { quantity: number; orderType: string; limitPrice?: number }) => {
      const res = await api.post(`/tokenization/trading/orders`, {
        tokenId,
        side: 'SELL',
        type: data.orderType.toUpperCase(),
        quantity: data.quantity,
        price: data.orderType === 'limit' ? data.limitPrice : undefined,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['token-holding', tokenId] });
      navigate('/banking/tokens/portfolio');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const parseCurrency = (value: string) => {
    return Math.round(parseFloat(value.replace(/[^\d,]/g, '').replace(',', '.')) * 100) || 0;
  };

  const sellQuantity = parseInt(quantity) || 0;
  const availableToSell = (holding?.quantity || 0) - (holding?.lockedQuantity || 0);
  const estimatedValue = sellQuantity * (holding?.unitPrice || 0);
  const tradingFee = Math.round(estimatedValue * (holding?.tradingFee || 0) / 100);
  const netProceeds = estimatedValue - tradingFee;

  const canSell = 
    sellQuantity > 0 &&
    sellQuantity <= availableToSell &&
    agreedToTerms;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!holding) {
    return (
      <div className="text-center py-12">
        <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
        <p className="text-surface-400">Você não possui este token</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/banking/tokens/${tokenId}`)}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Vender {holding.symbol}</h1>
          <p className="text-surface-400">{holding.name}</p>
        </div>
      </div>

      {/* Current Position */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
        <h3 className="text-sm font-medium text-surface-400 mb-3">Sua Posição</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-surface-400">Quantidade</p>
            <p className="font-semibold">{holding.quantity.toLocaleString('pt-BR')} {holding.symbol}</p>
          </div>
          <div>
            <p className="text-sm text-surface-400">Valor Atual</p>
            <p className="font-semibold">{formatCurrency(holding.currentValue)}</p>
          </div>
          <div>
            <p className="text-sm text-surface-400">Preço Médio</p>
            <p className="font-semibold">{formatCurrency(holding.avgBuyPrice)}</p>
          </div>
          <div>
            <p className="text-sm text-surface-400">Lucro/Prejuízo</p>
            <p className={`font-semibold ${holding.profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {holding.profitLoss >= 0 ? '+' : ''}{formatCurrency(holding.profitLoss)} ({holding.profitLossPercent.toFixed(2)}%)
            </p>
          </div>
        </div>
      </div>

      {/* Lock-up Warning */}
      {holding.lockedQuantity > 0 && (
        <div className="flex items-center gap-2 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">
            {holding.lockedQuantity.toLocaleString('pt-BR')} tokens estão em lock-up até {new Date(holding.lockUpEndDate!).toLocaleDateString('pt-BR')}
          </span>
        </div>
      )}

      {/* Order Form */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 p-6 space-y-6">
        {/* Order Type */}
        <div>
          <label className="block text-sm font-medium mb-2">Tipo de Ordem</label>
          <div className="flex gap-2">
            <button
              onClick={() => setOrderType('market')}
              className={`flex-1 py-2 rounded-lg transition-colors ${
                orderType === 'market'
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
              }`}
            >
              Mercado
            </button>
            <button
              onClick={() => setOrderType('limit')}
              className={`flex-1 py-2 rounded-lg transition-colors ${
                orderType === 'limit'
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
              }`}
            >
              Limitada
            </button>
          </div>
        </div>

        {/* Limit Price */}
        {orderType === 'limit' && (
          <div>
            <label className="block text-sm font-medium mb-2">Preço Limite</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">R$</span>
              <input
                type="text"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                placeholder="0,00"
                className="w-full pl-10 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg"
              />
            </div>
          </div>
        )}

        {/* Quantity Input */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">Quantidade a Vender</label>
            <span className="text-sm text-surface-400">
              Disponível: {availableToSell.toLocaleString('pt-BR')} {holding.symbol}
            </span>
          </div>
          
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="0"
            min="1"
            max={availableToSell}
            className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-lg"
          />

          <div className="flex gap-2 mt-2">
            {[25, 50, 75, 100].map((percent) => (
              <button
                key={percent}
                onClick={() => setQuantity(Math.floor(availableToSell * percent / 100).toString())}
                className="flex-1 py-1 text-sm bg-surface-800 hover:bg-surface-700 rounded transition-colors"
              >
                {percent}%
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-surface-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-surface-400">Quantidade</span>
            <span>{sellQuantity.toLocaleString('pt-BR')} {holding.symbol}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Preço atual</span>
            <span>{formatCurrency(holding.unitPrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Valor estimado</span>
            <span>{formatCurrency(estimatedValue)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Taxa ({holding.tradingFee}%)</span>
            <span className="text-red-400">-{formatCurrency(tradingFee)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-surface-700 font-semibold">
            <span>Você receberá</span>
            <span className="text-green-400">{formatCurrency(netProceeds)}</span>
          </div>
        </div>

        {/* Profit/Loss on Sale */}
        {sellQuantity > 0 && (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${
            (holding.unitPrice - holding.avgBuyPrice) >= 0
              ? 'bg-green-500/20 border border-green-500/50 text-green-400'
              : 'bg-red-500/20 border border-red-500/50 text-red-400'
          }`}>
            <Info className="w-4 h-4" />
            <span className="text-sm">
              {(holding.unitPrice - holding.avgBuyPrice) >= 0 ? 'Lucro' : 'Prejuízo'} estimado na venda: {' '}
              {formatCurrency(Math.abs((holding.unitPrice - holding.avgBuyPrice) * sellQuantity))}
            </span>
          </div>
        )}

        {/* Warnings */}
        {sellQuantity > availableToSell && (
          <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Quantidade excede o disponível para venda</span>
          </div>
        )}

        {/* Terms */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-surface-400">
            Confirmo que desejo vender os tokens selecionados e entendo que a operação é irreversível após execução.
          </span>
        </label>

        {/* Submit */}
        <button
          onClick={() => sellMutation.mutate({
            quantity: sellQuantity,
            orderType,
            limitPrice: orderType === 'limit' ? parseCurrency(limitPrice) : undefined,
          })}
          disabled={!canSell || sellMutation.isPending}
          className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors font-semibold"
        >
          {sellMutation.isPending ? 'Processando...' : `Vender ${sellQuantity} ${holding.symbol}`}
        </button>
      </div>
    </div>
  );
}

export default SellToken;
